import { Audio } from "../sfx/audio";
import type { MissionConfig } from "../mission/types";
import type { PreflightQuestion } from "../meta/preflight";

// Pre-mission briefing: mission name, briefing lines, squad strength, and
// active field mods — plus the optional hub-texture rituals: SIGINT analysis
// on hard missions (pick the actionable intercept) and the Pre-Flight Check
// (five yes/no questions, perfect score = the crew chief's blessing).
// Pure renderer; every outcome goes back through a flow callback.

export type SigintContext = {
  fragments: string[];
  // The flow judges the pick and applies any reward; returns the line to show.
  onPick: (index: number) => { correct: boolean; line: string };
};

export type PreflightContext = {
  questions: PreflightQuestion[];
  // Called once if all answers were right; the flow applies the bonus.
  onPassed: () => void;
};

export type PreMissionContext = {
  config: MissionConfig;
  squadLine: string; // e.g. "SQUAD 3/5"
  modLines: string[]; // active field-mod names for the readout
  sigint?: SigintContext;
  preflight?: PreflightContext;
  onStart: () => void;
  onBack: () => void;
};

type SigintState = { picked: number; correct: boolean; line: string } | null;
type PreflightState = { active: boolean; idx: number; wrong: number; done: boolean; passed: boolean };

export class PreMissionScreen {
  private readonly el: HTMLElement;
  private ctx: PreMissionContext | null = null;
  private muted = false;
  private sigintState: SigintState = null;
  private preflightState: PreflightState = { active: false, idx: 0, wrong: 0, done: false, passed: false };

  constructor() {
    this.el = document.createElement("div");
    this.el.id = "pre-mission";
    this.el.className = "screen-overlay";
    this.el.style.zIndex = "300";
    document.body.appendChild(this.el);
    this.el.addEventListener("click", this.handleClick);
  }

  private renderSigint(): string {
    const ctx = this.ctx;
    if (!ctx?.sigint) return "";
    if (this.sigintState) {
      return `
        <div class="sigint">
          <div class="sigint__title">SIGINT ANALYSIS — TRAFFIC READ</div>
          <div class="sigint__result sigint__result--${this.sigintState.correct ? "hit" : "miss"}">${this.sigintState.line}</div>
        </div>`;
    }
    const fragments = ctx.sigint.fragments
      .map((f, i) => `<button class="sigint__fragment" data-sigint="${i}">${f}</button>`)
      .join("");
    return `
      <div class="sigint">
        <div class="sigint__title">SIGINT ANALYSIS — WHICH INTERCEPT MATTERS? (OPTIONAL)</div>
        ${fragments}
      </div>`;
  }

  private renderPreflight(): string {
    const ctx = this.ctx;
    if (!ctx?.preflight) return "";
    const s = this.preflightState;
    if (s.done) {
      return `
        <div class="preflight">
          <div class="preflight__result preflight__result--${s.passed ? "hit" : "miss"}">
            ${s.passed ? "PRE-FLIGHT COMPLETE — CHIEF'S BLESSING: +1 FLARE" : "PRE-FLIGHT COMPLETE — THE CHIEF SIGHS AND SIGNS ANYWAY"}
          </div>
        </div>`;
    }
    if (!s.active) {
      return `
        <div class="preflight">
          <button id="pm-preflight" class="screen-button screen-button--small screen-button--muted">PRE-FLIGHT CHECK (OPTIONAL — 5 QUESTIONS, ALL RIGHT = +1 FLARE)</button>
        </div>`;
    }
    const q = ctx.preflight.questions[s.idx]!;
    return `
      <div class="preflight">
        <div class="preflight__q">CHIEF: &nbsp;${q.q} &nbsp;<span class="preflight__count">(${s.idx + 1}/${ctx.preflight.questions.length})</span></div>
        <div class="preflight__answers">
          <button class="screen-button screen-button--small" data-preflight="yes">YES</button>
          <button class="screen-button screen-button--small" data-preflight="no">NO</button>
        </div>
      </div>`;
  }

  private render(): void {
    const ctx = this.ctx;
    if (!ctx) return;

    const briefing = ctx.config.briefing?.length
      ? `<div class="pre-mission-briefing">${ctx.config.briefing
          .map((line) => `<div class="pre-mission-briefing__line">▸ ${line}</div>`)
          .join("")}</div>`
      : "";

    const mods = ctx.modLines.length
      ? `<div class="pre-mission-mods">${ctx.modLines
          .map((line) => `<span class="run-mod-chip">${line}</span>`)
          .join("")}</div>`
      : "";

    this.el.innerHTML = `
      <div class="screen-panel">
        <div class="screen-kicker">TOP SECRET // NOFORN</div>
        <div class="screen-title screen-title--compact">${ctx.config.name}</div>
        <div class="screen-subtitle">${ctx.squadLine}</div>
        ${briefing}
        ${mods}
        ${this.renderSigint()}
        ${this.renderPreflight()}
        <div class="screen-actions" style="margin-top:16px">
          <button id="pm-start" class="screen-button screen-button--large">START MISSION</button>
          <button id="pm-back" class="screen-button screen-button--muted">BACK</button>
          <button id="pm-mute" class="screen-button screen-button--muted">${this.muted ? "UNMUTE" : "MUTE"}</button>
        </div>
        <div class="screen-footer">CLICK CANVAS TO LOCK MOUSE &nbsp;|&nbsp; ESC = PAUSE &nbsp;|&nbsp; 1/2/3 = WEAPON &nbsp;|&nbsp; H = HOLD/GO</div>
      </div>
    `;
  }

  show(ctx: PreMissionContext): void {
    this.ctx = ctx;
    this.sigintState = null;
    this.preflightState = { active: false, idx: 0, wrong: 0, done: false, passed: false };
    this.render();
    this.el.classList.add("is-visible");
  }

  hide(): void {
    this.el.classList.remove("is-visible");
  }

  private toggleMute(): void {
    this.muted = !this.muted;
    Audio.setVolume(this.muted ? 0 : 1);
    this.render();
  }

  private answerPreflight(saidYes: boolean): void {
    const ctx = this.ctx;
    if (!ctx?.preflight) return;
    const s = this.preflightState;
    const q = ctx.preflight.questions[s.idx]!;
    if (q.yes !== saidYes) s.wrong++;
    s.idx++;
    if (s.idx >= ctx.preflight.questions.length) {
      s.done = true;
      s.passed = s.wrong === 0;
      if (s.passed) ctx.preflight.onPassed();
    }
    this.render();
  }

  private handleClick = (event: MouseEvent): void => {
    const target = event.target as HTMLElement | null;
    if (!target || !this.ctx) return;

    const sigintIdx = target.closest<HTMLElement>("[data-sigint]")?.dataset["sigint"];
    if (sigintIdx !== undefined && this.ctx.sigint && !this.sigintState) {
      const picked = Number(sigintIdx);
      const result = this.ctx.sigint.onPick(picked);
      this.sigintState = { picked, ...result };
      this.render();
      return;
    }

    if (target.closest("#pm-preflight")) {
      this.preflightState.active = true;
      this.render();
      return;
    }
    const answer = target.closest<HTMLElement>("[data-preflight]")?.dataset["preflight"];
    if (answer) {
      this.answerPreflight(answer === "yes");
      return;
    }

    if (target.closest("#pm-start")) {
      const ctx = this.ctx;
      this.hide();
      ctx.onStart();
    } else if (target.closest("#pm-back")) {
      const ctx = this.ctx;
      this.hide();
      ctx.onBack();
    } else if (target.closest("#pm-mute")) {
      this.toggleMute();
    }
  };
}
